import { Component, OnInit, OnDestroy, ChangeDetectorRef, AnimationCallbackEvent, input, signal, inject } from '@angular/core';
import moment from 'moment';
import { gsap } from 'gsap';
import {
    IDispatchCampaign,
    IDispatchCampaignTargetFilterCondition
} from '../../../../../../../../../../common/common-interfaces/admin/crm/dispatch-campaign';
import { IClient } from '../../../../../../../../../../common/common-interfaces/client';
import { Api } from '../../../../../services/api.service';
import { IPackage } from '../../../../../../../../../../common/common-interfaces/package';
import { DatePickerModule } from 'primeng/datepicker';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectModule } from 'primeng/select';
import { debugLogger } from '@common-helpers/debug-logger';

@Component({
    selector: 'dispatch-campaign-target-filter-mode-editor',
    templateUrl: './target-filter-mode-editor.component.html',
    styleUrls: ['./target-filter-mode-editor.component.scss'],
    standalone: true,
    imports: [MultiSelectModule, SelectModule, DatePickerModule, CommonModule, FormsModule, TranslatePipe]
})
export class DispatchCampaignTargetFilterModeEditorComponent implements OnInit, OnDestroy {
    api = inject(Api);
    cdr = inject(ChangeDetectorRef);

    dispatchCampaign = input.required<IDispatchCampaign>();
    filterMode = input<string>('');
    clients = input.required<IClient[]>();
    packagesLoading = signal<boolean>(false);

    availablePackagesByClient: { [clientId: string]: any[] } = {};

    async ngOnInit(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign?.targetFilter) {
            debugLogger.warn('Mode editor initialized without a targetFilter on dispatchCampaign');
            return;
        }

        debugLogger.info(`Mode editor initialized with mode ${dispatchCampaign.targetFilter.mode}`);

        // modeSettings itself might not exist yet
        if (!dispatchCampaign.targetFilter.modeSettings) {
            dispatchCampaign.targetFilter.modeSettings = {};
        }
        const modeSettings = dispatchCampaign.targetFilter.modeSettings;

        switch (dispatchCampaign.targetFilter.mode) {
            case 'SPECIFIC_PACKAGES':
                if (modeSettings.SPECIFIC_PACKAGES) {
                    this.packagesLoading.set(true);
                    if (modeSettings.SPECIFIC_PACKAGES.clientId) {
                        const packages = await this.api.client(modeSettings.SPECIFIC_PACKAGES.clientId).get<any[]>(`/packages`);
                        if (packages.data) {
                            this.availablePackagesByClient[modeSettings.SPECIFIC_PACKAGES.clientId] = packages.data;
                        }
                        this.packagesLoading.set(false);
                    } else {
                        const clients = this.clients();
                        if (clients?.length === 1 && clients[0]?.id) {
                            modeSettings.SPECIFIC_PACKAGES.clientId = clients[0].id;
                            await this.SPECIFIC_PACKAGES_clientIdChanged();
                        } else {
                            this.packagesLoading.set(false);
                        }
                    }
                } else {
                    modeSettings.SPECIFIC_PACKAGES = { packageIds: [] };
                    const clients = this.clients();
                    if (clients?.length === 1 && clients[0]?.id) {
                        modeSettings.SPECIFIC_PACKAGES.clientId = clients[0].id;
                        await this.SPECIFIC_PACKAGES_clientIdChanged();
                    }
                }
                break;
            case 'ALL_CUSTOMERS':
                if (modeSettings.ALL_CUSTOMERS) {
                    if (!modeSettings.ALL_CUSTOMERS.clientIds?.length) {
                        modeSettings.ALL_CUSTOMERS.clientIds = (this.clients() ?? []).map((c) => c.id).filter((id): id is string => !!id);
                    }
                } else {
                    modeSettings.ALL_CUSTOMERS = {
                        clientIds: (this.clients() ?? []).map((c) => c.id).filter((id): id is string => !!id)
                    };
                }
                await this.ALL_CUSTOMERS_clientIdsChanged();
                break;
            case 'NOT_BOOKED_SINCE':
                if (!modeSettings.NOT_BOOKED_SINCE) {
                    modeSettings.NOT_BOOKED_SINCE = {
                        clientIds: [],
                        notBookedSince: moment().subtract(3, 'months').toDate()
                    };
                }
                break;
        }

        await this.rebuildConditions();
        this.cdr.markForCheck();
    }

    async ALL_CUSTOMERS_clientIdsChanged(): Promise<void> {
        await this.rebuildConditions();
        this.cdr.markForCheck();
    }

    async SPECIFIC_PACKAGES_clientIdChanged(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        const specificPackages = dispatchCampaign?.targetFilter?.modeSettings?.SPECIFIC_PACKAGES;
        if (!specificPackages) {
            debugLogger.warn('SPECIFIC_PACKAGES_clientIdChanged called with no SPECIFIC_PACKAGES mode settings present');
            return;
        }

        this.packagesLoading.set(true);
        specificPackages.packageIds = [];

        const clientId = specificPackages.clientId;
        if (clientId && !this.availablePackagesByClient[clientId]) {
            let packageData = await this.api.client(clientId).get<IPackage[]>(`/packages`);
            if (packageData.success && packageData.data) {
                this.availablePackagesByClient[clientId] = packageData.data;
            }
        }

        await this.rebuildConditions();
        this.packagesLoading.set(false);
    }

    async SPECIFIC_PACKAGES_packageIdsChanged(): Promise<void> {
        debugLogger.info('Package IDs changed');
        await this.rebuildConditions();
        this.cdr.markForCheck();
    }

    async NOT_BOOKED_SINCE_clientIdsChanged(): Promise<void> {
        await this.rebuildConditions();
        this.cdr.markForCheck();
    }

    async NOT_BOOKED_SINCE_notBookedSinceChanged(): Promise<void> {
        await this.rebuildConditions();
        this.cdr.markForCheck();
    }

    async rebuildConditions(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        const targetFilter = dispatchCampaign?.targetFilter;
        if (!targetFilter) {
            debugLogger.warn('rebuildConditions called with no targetFilter present');
            return;
        }

        debugLogger.info(`Rebuilding conditions for mode ${targetFilter.mode}`);
        const modeSettings = targetFilter.modeSettings;

        switch (targetFilter.mode) {
            case 'ALL_CUSTOMERS': {
                targetFilter.conditions = [];
                const clientIds = modeSettings?.ALL_CUSTOMERS?.clientIds ?? [];
                for (const clientId of clientIds) {
                    targetFilter.conditions.push({
                        action: 'INCLUDE',
                        clientId: clientId,
                        type: 'ALL_CUSTOMERS',
                        data: {
                            ALL_CUSTOMERS: {
                                newsletterAcceptedOnly: true
                            }
                        }
                    } as IDispatchCampaignTargetFilterCondition);
                }
                break;
            }
            case 'SPECIFIC_PACKAGES': {
                targetFilter.conditions = [];
                const specificPackages = modeSettings?.SPECIFIC_PACKAGES;
                if (specificPackages?.packageIds?.length && specificPackages.clientId) {
                    targetFilter.conditions.push({
                        action: 'INCLUDE',
                        clientId: specificPackages.clientId,
                        type: 'SPECIFIC_BOOKINGS',
                        data: {
                            SPECIFIC_BOOKINGS: {
                                newsletterAcceptedOnly: true,
                                includeInvitedpeople: true,
                                bookingDate: {
                                    enabled: false
                                },
                                visitDate: {
                                    enabled: false
                                },
                                packageIds: specificPackages.packageIds
                            }
                        }
                    } as IDispatchCampaignTargetFilterCondition);
                }
                break;
            }
            case 'NOT_BOOKED_SINCE': {
                targetFilter.conditions = [];
                const notBookedSince = modeSettings?.NOT_BOOKED_SINCE;
                const clientIds = notBookedSince?.clientIds ?? [];
                for (const clientId of clientIds) {
                    targetFilter.conditions.push({
                        action: 'INCLUDE',
                        clientId: clientId,
                        type: 'ALL_CUSTOMERS',
                        data: {
                            ALL_CUSTOMERS: {
                                newsletterAcceptedOnly: true
                            }
                        }
                    } as IDispatchCampaignTargetFilterCondition);

                    targetFilter.conditions.push({
                        action: 'EXCLUDE',
                        clientId: clientId,
                        type: 'SPECIFIC_BOOKINGS',
                        data: {
                            SPECIFIC_BOOKINGS: {
                                newsletterAcceptedOnly: true,
                                bookingDate: {
                                    enabled: true,
                                    start: notBookedSince?.notBookedSince,
                                    end: moment().add(1, 'year').toDate()
                                },
                                visitDate: {
                                    enabled: false
                                }
                            }
                        }
                    } as IDispatchCampaignTargetFilterCondition);
                }
                break;
            }
        }
        this.cdr.markForCheck();
    }

    async back(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        if (dispatchCampaign?.targetFilter) {
            delete dispatchCampaign.targetFilter.mode;
        }
        this.cdr.markForCheck();
    }

    ngOnDestroy(): void {
        // No cleanup currently needed. Left as a stub in case
        // subscriptions/timers are added here later.
    }

    animateStepIn(event: AnimationCallbackEvent): void {
        gsap.fromTo(
            event.target.children,
            {
                opacity: 0,
                y: 10
            },
            {
                opacity: 1,
                y: 0,
                duration: 0.3,
                ease: 'power2.out',
                stagger: 0.08
            }
        );
    }
}
